import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2, Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";
import { createTermMultipart } from "@/lib/termsApi";
import type { Term } from "../types";
import {
  AdminTermsTable,
  type AdminTermsServerPagination,
} from "../components/AdminTermsTable";
import { WordMasterImportMappingDialog } from "../components/WordMasterImportMappingDialog";
import {
  WordMasterImportProgressDialog,
  type ImportProgressState,
} from "../components/WordMasterImportProgressDialog";
import {
  applyWordMasterMappingIndexed,
  downloadBlob,
  exportImportFailuresToXlsxBlob,
  exportTermsToXlsxBlob,
  inspectWordMasterWorkbook,
  type WordMasterColumnMapping,
  type WordMasterImportFailure,
} from "../wordMasterExcel";

/** Concurrent term-create requests during a bulk import. */
const IMPORT_CONCURRENCY = 8;

export interface AdminWordMasterPageProps {
  terms: Term[];
  isLoading: boolean;
  loadProgress?: { loaded: number; total: number | null; complete: boolean };
  serverPagination: AdminTermsServerPagination;
  onAdd: () => void;
  onEdit: (term: Term) => void;
  onDelete: (term: Term) => void;
  canDeleteTerms: boolean;
  onStatusChange: (
    term: Term,
    next: "pending" | "approved" | "rejected"
  ) => void;
  onRefresh: () => void;
  onTermUpdated?: (term: Term) => void;
}

export function AdminWordMasterPage({
  terms,
  isLoading,
  loadProgress,
  serverPagination,
  onAdd,
  onEdit,
  onDelete,
  canDeleteTerms,
  onStatusChange,
  onRefresh,
  onTermUpdated,
}: AdminWordMasterPageProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [pendingImportRows, setPendingImportRows] = useState<
    Record<string, unknown>[]
  >([]);
  const [pendingExcelColumns, setPendingExcelColumns] = useState<string[]>([]);

  const [exportSelectionIds, setExportSelectionIds] = useState<Set<string>>(
    () => new Set()
  );

  // Live bulk-import progress (null when no import is in flight or being reviewed).
  const [importRun, setImportRun] = useState<ImportProgressState | null>(null);
  const importFailuresRef = useRef<WordMasterImportFailure[]>([]);
  const cancelImportRef = useRef(false);

  useEffect(() => {
    const valid = new Set(terms.map(t => t.id));
    setExportSelectionIds(prev => {
      let pruned = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (valid.has(id)) next.add(id);
        else pruned = true;
      }
      return pruned ? next : prev;
    });
  }, [terms]);

  const selectedTermsForExport = useMemo(
    () => terms.filter(t => exportSelectionIds.has(t.id)),
    [terms, exportSelectionIds]
  );

  const handleExport = () => {
    const list = selectedTermsForExport;
    if (list.length === 0) return;
    const blob = exportTermsToXlsxBlob(list);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadBlob(blob, `lexicon-word-master-${stamp}.xlsx`);
    toast.success(
      `Exported ${list.length} selected term${list.length === 1 ? "" : "s"}`
    );
  };

  const handleImportFileChosen = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const { columns, rows } = inspectWordMasterWorkbook(buffer);
      if (columns.length === 0) {
        toastApiError(
          "No columns found. Use the first sheet with a header row."
        );
        return;
      }
      setPendingExcelColumns(columns);
      setPendingImportRows(rows);
      setMappingDialogOpen(true);
    } catch (err) {
      console.error(err);
      toastApiError("Could not read the spreadsheet file.");
    }
  };

  const runImportWithMapping = async (mapping: WordMasterColumnMapping) => {
    const rowsSnapshot = pendingImportRows;
    setMappingDialogOpen(false);
    setPendingExcelColumns([]);
    setPendingImportRows([]);

    if (!mapping.word) {
      toastApiError('Map the "Word" field to a spreadsheet column before importing.');
      return;
    }

    const parsed = applyWordMasterMappingIndexed(rowsSnapshot, mapping);
    if (parsed.length === 0) {
      toastApiError(
        "No importable rows (each row needs a non-empty value in the mapped Word column)."
      );
      return;
    }

    const total = parsed.length;
    importFailuresRef.current = [];
    cancelImportRef.current = false;
    setImporting(true);
    setImportRun({
      phase: "running",
      total,
      imported: 0,
      failed: 0,
      cancelled: false,
    });

    let imported = 0;
    let failed = 0;
    let cursor = 0;

    // Throttle UI updates: 100k rows must not trigger 100k re-renders.
    const flush = () =>
      setImportRun(prev => (prev ? { ...prev, imported, failed } : prev));
    const ticker = window.setInterval(flush, 150);

    // Worker pulls the next row until the queue drains or the user cancels.
    const worker = async () => {
      for (;;) {
        if (cancelImportRef.current) return;
        const index = cursor;
        cursor += 1;
        if (index >= total) return;
        const { row, sheetRow } = parsed[index];
        try {
          const { ok, error } = await createTermMultipart(
            {
              word: row.word,
              meaning_english: row.meaning_english,
              meaning_hindi: row.meaning_hindi,
              meaning_gujarati: row.meaning_gujarati,
              pronunciation: row.pronunciation,
              example_usage: row.example_usage,
              status: row.status,
              contributor_name: row.contributor_name,
              contributor_email: row.contributor_email,
              is_featured: row.is_featured,
              is_enabled: row.is_enabled,
              ...(row.admin_notes.trim()
                ? { admin_notes: row.admin_notes }
                : {}),
            },
            null,
            "staff"
          );
          if (ok) {
            imported += 1;
          } else {
            failed += 1;
            importFailuresRef.current.push({
              sheetRow,
              row,
              error: error ?? "Unknown error",
            });
          }
        } catch (err) {
          failed += 1;
          importFailuresRef.current.push({
            sheetRow,
            row,
            error: err instanceof Error ? err.message : "Network error",
          });
        }
      }
    };

    try {
      await Promise.all(
        Array.from({ length: Math.min(IMPORT_CONCURRENCY, total) }, worker)
      );
    } finally {
      window.clearInterval(ticker);
      setImporting(false);
      setImportRun({
        phase: "done",
        total,
        imported,
        failed,
        cancelled: cancelImportRef.current,
      });
      // Refresh the table if anything landed (even on a cancelled/partial run).
      if (imported > 0) {
        onRefresh();
      }
    }
  };

  const handleCancelImport = () => {
    cancelImportRef.current = true;
  };

  const handleCloseImport = () => {
    setImportRun(null);
    importFailuresRef.current = [];
  };

  const handleDownloadFailures = () => {
    const failures = importFailuresRef.current;
    if (failures.length === 0) return;
    const blob = exportImportFailuresToXlsxBlob(failures);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadBlob(blob, `lexicon-import-failures-${stamp}.xlsx`);
  };

  const busy = isLoading || importing;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="mb-1 font-serif text-2xl font-bold">Word Master</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || exportSelectionIds.size === 0}
            onClick={handleExport}
            className="gap-1.5"
            title={
              exportSelectionIds.size === 0
                ? "Select one or more rows in the table to export"
                : undefined
            }
          >
            <Download className="h-4 w-4 shrink-0" />
            Export Excel
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || mappingDialogOpen}
            onClick={() => fileInputRef.current?.click()}
            className="gap-1.5"
          >
            {importing ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 shrink-0" />
            )}
            Import Excel
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="hidden"
            onChange={handleImportFileChosen}
          />
          <Button onClick={onAdd} className="bg-primary hover:bg-primary/90">
            <Plus className="mr-2 h-4 w-4" /> Add New Term
          </Button>
        </div>
      </div>
      <AdminTermsTable
        terms={terms}
        status="all"
        isLoading={isLoading}
        loadProgress={loadProgress}
        serverPagination={serverPagination}
        showFeatured
        showDeleteActions={canDeleteTerms}
        rowSelection={{
          selectedIds: exportSelectionIds,
          onChange: setExportSelectionIds,
        }}
        onEdit={onEdit}
        onDelete={onDelete}
        onStatusChange={onStatusChange}
        onRefresh={onRefresh}
        onTermUpdated={onTermUpdated}
      />
      <WordMasterImportMappingDialog
        open={mappingDialogOpen}
        onOpenChange={open => {
          setMappingDialogOpen(open);
          if (!open) {
            setPendingImportRows([]);
            setPendingExcelColumns([]);
          }
        }}
        excelColumns={pendingExcelColumns}
        rawRowCount={pendingImportRows.length}
        onConfirm={runImportWithMapping}
      />

      <WordMasterImportProgressDialog
        state={importRun}
        onCancel={handleCancelImport}
        onClose={handleCloseImport}
        onDownloadFailures={handleDownloadFailures}
      />
    </div>
  );
}
